import React, { useState, useMemo } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getEmailCampaigns,
  getEmailCampaignAudiences,
  createEmailCampaign,
  updateEmailCampaign,
  scheduleEmailCampaign,
  cancelEmailCampaign,
  deleteEmailCampaign,
} from '../../endpoints/emailCampaigns';
import type {
  EmailCampaignListItem,
  CreateEmailCampaignRequest,
  UpdateEmailCampaignRequest,
  ScheduleEmailCampaignRequest,
  Audience,
  ListEmailCampaignsResult,
} from '../../endpoints/emailCampaigns';
import AlertItem from '../../Components/AlertItem';
import dayjs from 'dayjs';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending', label: 'Sending' },
  { value: 'sent', label: 'Sent' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
];

const EmailCampaignsList: React.FC = () => {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editCampaign, setEditCampaign] =
    useState<EmailCampaignListItem | null>(null);
  const [scheduleCampaign, setScheduleCampaign] =
    useState<EmailCampaignListItem | null>(null);
  const [cancelCampaign, setCancelCampaign] =
    useState<EmailCampaignListItem | null>(null);
  const [deleteCampaign, setDeleteCampaign] =
    useState<EmailCampaignListItem | null>(null);
  const [formName, setFormName] = useState('');
  const [formTemplateId, setFormTemplateId] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formAudienceIds, setFormAudienceIds] = useState<string[]>([]);
  const [scheduleAt, setScheduleAt] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const navigate = useNavigate();
  const listParams = useMemo(
    () => ({
      page: page + 1,
      per_page: perPage,
      status: statusFilter || undefined,
      search: searchDebounce || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    }),
    [page, perPage, statusFilter, searchDebounce, startDate, endDate]
  );

  const {
    data: listData,
    isLoading,
    error,
    refetch,
  } = useApiGet<ListEmailCampaignsResult>(
    ['email-campaigns', listParams],
    () => getEmailCampaigns(listParams),
    { enabled: true, retry: 1 }
  );

  const { data: audiences } = useApiGet<Audience[]>(
    ['email-campaign-audiences'],
    getEmailCampaignAudiences,
    { enabled: createOpen || editCampaign !== null }
  );

  const listItems = listData?.items ?? [];
  const totalCount = listData?.total_count ?? 0;

  const invalidateList = () => {
    refetch();
  };

  const { mutate: createMutate } = useApiSend(
    (data: CreateEmailCampaignRequest) => createEmailCampaign(data),
    () => {
      setCreateOpen(false);
      setFormName('');
      setFormTemplateId('');
      setFormSubject('');
      setFormAudienceIds([]);
      setIsSaving(false);
      setAlertState({
        severity: 'success',
        description: 'Campaign created.',
      });
      invalidateList();
    },
    (err: Error) => {
      setIsSaving(false);
      setAlertState({
        severity: 'error',
        description: (err as any)?.error || err?.message || 'Create failed.',
      });
    },
    [['email-campaigns']]
  );

  const { mutate: updateMutate } = useApiSend(
    ({ id, data }: { id: string; data: UpdateEmailCampaignRequest }) =>
      updateEmailCampaign(id, data),
    () => {
      setEditCampaign(null);
      setFormName('');
      setFormTemplateId('');
      setFormSubject('');
      setFormAudienceIds([]);
      setIsSaving(false);
      setAlertState({ severity: 'success', description: 'Campaign updated.' });
      invalidateList();
    },
    (err: Error) => {
      setIsSaving(false);
      setAlertState({
        severity: 'error',
        description: (err as any)?.error || err?.message || 'Update failed.',
      });
    },
    [['email-campaigns']]
  );

  const { mutate: scheduleMutate } = useApiSend(
    ({ id, data }: { id: string; data: ScheduleEmailCampaignRequest }) =>
      scheduleEmailCampaign(id, data),
    () => {
      setScheduleCampaign(null);
      setScheduleAt('');
      setIsSaving(false);
      setAlertState({
        severity: 'success',
        description: 'Campaign scheduled.',
      });
      invalidateList();
    },
    (err: Error) => {
      setIsSaving(false);
      setAlertState({
        severity: 'error',
        description: (err as any)?.error || err?.message || 'Schedule failed.',
      });
    },
    [['email-campaigns']]
  );

  const { mutate: cancelMutate } = useApiSend(
    (id: string) => cancelEmailCampaign(id),
    () => {
      setCancelCampaign(null);
      setAlertState({
        severity: 'success',
        description: 'Campaign cancelled.',
      });
      invalidateList();
    },
    (err: Error) => {
      setAlertState({
        severity: 'error',
        description: (err as any)?.error || err?.message || 'Cancel failed.',
      });
    },
    [['email-campaigns']]
  );

  const { mutate: deleteMutate } = useApiSend(
    (id: string) => deleteEmailCampaign(id),
    () => {
      setDeleteCampaign(null);
      setAlertState({ severity: 'success', description: 'Campaign deleted.' });
      invalidateList();
    },
    (err: Error) => {
      setAlertState({
        severity: 'error',
        description: (err as any)?.error || err?.message || 'Delete failed.',
      });
    },
    [['email-campaigns']]
  );

  const handleCreateOpen = () => {
    setFormName('');
    setFormTemplateId('');
    setFormSubject('');
    setFormAudienceIds([]);
    setCreateOpen(true);
  };

  const handleCreateSubmit = () => {
    if (!formName.trim()) {
      setAlertState({
        severity: 'error',
        description: 'Name is required.',
      });
      return;
    }
    if (formAudienceIds.length === 0) {
      setAlertState({
        severity: 'error',
        description: 'Select at least one audience.',
      });
      return;
    }
    setIsSaving(true);
    createMutate({
      name: formName.trim(),
      template_id: formTemplateId.trim() || undefined,
      subject: formSubject.trim() || undefined,
      audience_ids: formAudienceIds,
    });
  };

  const handleEditOpen = (row: EmailCampaignListItem) => {
    setEditCampaign(row);
    setFormName(row.name);
    setFormTemplateId(row.template_id ?? '');
    setFormSubject(row.subject ?? '');
    setFormAudienceIds(
      row.audience_ids ? row.audience_ids.split(',').map((s) => s.trim()) : []
    );
  };

  const handleEditSubmit = () => {
    if (!editCampaign) return;
    if (!formName.trim()) {
      setAlertState({
        severity: 'error',
        description: 'Name is required.',
      });
      return;
    }
    if (formAudienceIds.length === 0) {
      setAlertState({
        severity: 'error',
        description: 'Select at least one audience.',
      });
      return;
    }
    setIsSaving(true);
    updateMutate({
      id: editCampaign.id,
      data: {
        name: formName.trim(),
        template_id: formTemplateId.trim() || undefined,
        subject: formSubject.trim() || undefined,
        audience_ids: formAudienceIds,
      },
    });
  };

  const handleScheduleOpen = (row: EmailCampaignListItem) => {
    setScheduleCampaign(row);
    setScheduleAt('');
  };

  const handleScheduleSubmit = () => {
    if (!scheduleCampaign) return;
    const at = scheduleAt.trim();
    if (!at) {
      setAlertState({
        severity: 'error',
        description: 'Scheduled date/time is required.',
      });
      return;
    }
    // datetime-local is in user's local time; send RFC3339 with timezone so server has unambiguous instant
    const date = new Date(at);
    if (Number.isNaN(date.getTime())) {
      setAlertState({
        severity: 'error',
        description: 'Invalid date/time.',
      });
      return;
    }
    setIsSaving(true);
    scheduleMutate({
      id: scheduleCampaign.id,
      data: { scheduled_at: date.toISOString() },
    });
  };

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    try {
      return dayjs(dateString).format('MMM DD, YYYY h:mm A');
    } catch {
      return '—';
    }
  };

  const isEditable = (row: EmailCampaignListItem) =>
    row.status === 'draft' || row.status === 'scheduled';
  const canSchedule = (row: EmailCampaignListItem) => row.status === 'draft';
  const canCancel = (row: EmailCampaignListItem) =>
    row.status === 'scheduled' || row.status === 'sending';
  const canDelete = (row: EmailCampaignListItem) => row.status === 'draft';

  React.useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={2}>
        <Typography variant="h4" color="primary">
          Email Campaigns
        </Typography>
      </Grid>

      {alertState && (
        <Grid item xs={12}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateOpen}
            >
              Create campaign
            </Button>
            <TextField
              size="small"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              label="Start date"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              label="End date"
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load campaigns. Please try again.
            </Alert>
          )}

          {!isLoading && !error && (
            <>
              {listItems.length === 0 ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ py: 4 }}
                >
                  No campaigns found.
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Subject</TableCell>
                          <TableCell>Est. recipients</TableCell>
                          <TableCell>Scheduled</TableCell>
                          <TableCell>Sent</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {listItems.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.status}
                                size="small"
                                variant="outlined"
                                color={
                                  row.status === 'sent'
                                    ? 'success'
                                    : row.status === 'failed'
                                      ? 'error'
                                      : row.status === 'scheduled'
                                        ? 'info'
                                        : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell>{row.subject || '—'}</TableCell>
                            <TableCell>
                              {row.estimated_recipient_count ?? '—'}
                            </TableCell>
                            <TableCell>
                              {formatDateTime(row.scheduled_at)}
                            </TableCell>
                            <TableCell>{formatDateTime(row.sent_at)}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                title="View"
                                onClick={() =>
                                  navigate(
                                    `/administration/email-campaigns/${row.id}`
                                  )
                                }
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              {isEditable(row) && (
                                <IconButton
                                  size="small"
                                  title="Edit"
                                  onClick={() => handleEditOpen(row)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {canSchedule(row) && (
                                <IconButton
                                  size="small"
                                  title="Schedule"
                                  onClick={() => handleScheduleOpen(row)}
                                >
                                  <ScheduleIcon fontSize="small" />
                                </IconButton>
                              )}
                              {canCancel(row) && (
                                <IconButton
                                  size="small"
                                  title="Cancel"
                                  onClick={() => setCancelCampaign(row)}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              )}
                              {canDelete(row) && (
                                <IconButton
                                  size="small"
                                  title="Delete"
                                  onClick={() => setDeleteCampaign(row)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => {
                      setPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </>
              )}
            </>
          )}
        </Paper>
      </Grid>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create email campaign</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            required
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Template ID"
            value={formTemplateId}
            onChange={(e) => setFormTemplateId(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Subject"
            value={formSubject}
            onChange={(e) => setFormSubject(e.target.value)}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Audiences</InputLabel>
            <Select
              multiple
              value={formAudienceIds}
              onChange={(e) =>
                setFormAudienceIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value
                )
              }
              input={<OutlinedInput label="Audiences" />}
              renderValue={(selected) =>
                (audiences ?? [])
                  .filter((a) => selected.includes(a.id))
                  .map((a) => a.name)
                  .join(', ')
              }
            >
              {(audiences ?? []).map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  <Checkbox checked={formAudienceIds.indexOf(a.id) > -1} />
                  <ListItemText primary={a.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={editCampaign !== null}
        onClose={() => setEditCampaign(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit campaign</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            required
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Template ID"
            value={formTemplateId}
            onChange={(e) => setFormTemplateId(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Subject"
            value={formSubject}
            onChange={(e) => setFormSubject(e.target.value)}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Audiences</InputLabel>
            <Select
              multiple
              value={formAudienceIds}
              onChange={(e) =>
                setFormAudienceIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value
                )
              }
              input={<OutlinedInput label="Audiences" />}
              renderValue={(selected) =>
                (audiences ?? [])
                  .filter((a) => selected.includes(a.id))
                  .map((a) => a.name)
                  .join(', ')
              }
            >
              {(audiences ?? []).map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  <Checkbox checked={formAudienceIds.indexOf(a.id) > -1} />
                  <ListItemText primary={a.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCampaign(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog
        open={scheduleCampaign !== null}
        onClose={() => setScheduleCampaign(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule campaign</DialogTitle>
        <DialogContent>
          {scheduleCampaign && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {scheduleCampaign.name}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Scheduled at"
            type="datetime-local"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleCampaign(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleScheduleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Scheduling…' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirm */}
      <Dialog
        open={cancelCampaign !== null}
        onClose={() => setCancelCampaign(null)}
      >
        <DialogTitle>Cancel campaign?</DialogTitle>
        <DialogContent>
          {cancelCampaign && (
            <Typography>
              Cancel &quot;{cancelCampaign.name}&quot;? It will not be sent.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelCampaign(null)}>No</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (cancelCampaign) {
                cancelMutate(cancelCampaign.id);
                setCancelCampaign(null);
              }
            }}
          >
            Yes, cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={deleteCampaign !== null}
        onClose={() => setDeleteCampaign(null)}
      >
        <DialogTitle>Delete campaign?</DialogTitle>
        <DialogContent>
          {deleteCampaign && (
            <Typography>
              Permanently delete &quot;{deleteCampaign.name}&quot;?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCampaign(null)}>No</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (deleteCampaign) {
                deleteMutate(deleteCampaign.id);
                setDeleteCampaign(null);
              }
            }}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default EmailCampaignsList;
